<template>
  <div
    class="hcl-toolbar-content-actions"
    v-if="props.definition.type === 'actions'"
  >
    <div
      v-if="props.definition.icon || props.definition.text"
      class="toolbar-content-actions-text"
      :class="props.definition.css"
    >
      <span
        class="content-actions-text-image"
        v-if="props.definition.icon !== ''"
        :class="props.definition.icon"
      ></span>
      <div
        v-if="props.definition.text !== ''"
        class="content-actions-text-text"
      >
        {{ props.definition.text }}
      </div>
    </div>
    <template v-for="action in props.definition.actions">
      <hcl-button-group
        v-if="action.type === 'group'"
        :key="action.type + '-' + action.id"
        :id="action.id"
      >
        <template
          v-for="item in action.items"
          :key="item.type + '-' + item.id"
        >
          <hcl-button
            :type="item.type"
            :variant="item.variant"
            :id="item.id"
            :toolbar-id="toolbarMetaId"
            :title="item.title"
            :disabled="item.disabled"
            :hidden="item.hidden"
            :responsive="item.responsive"
            :href="item.href"
            :label="item.label"
            :icon="item.icon"
            :menu-items="item.menuItems"
            :widget-element="item.widgetElement"
            :widget-attributes="item.widgetAttributes"
            class="spacer"
            v-on:hcl-button-clicked="onButtonClicked"
            v-on:hcl-list-global-select-all-changed="onListGlobalSelectAllChanged"
            :data="props.data"
          ></hcl-button>
        </template>
      </hcl-button-group>
      <hcl-favorites
        v-if="action.type === 'favorites'"
        :key="action.type + '-' + action.id"
        :id="action.id"
        :favorite-type="action.favoriteType"
        :favorite-id="bucketToolbar?.favorites?.id"
        :favorite-options="bucketToolbar?.favorites?.options"
        :favorite-id-no-selection="action.favoriteIdNoSelection"
        :favorite-form-id="action.favoriteFormId"
        :urls-load="action.urls.load"
        :urls-save="action.urls.save"
        :urls-delete="action.urls.delete"
        :urls-redirect="action.urls.redirect"
        :message-modal-title-add="action.messages.modal.title.add"
        :message-modal-title-edit="action.messages.modal.title.edit"
        :message-modal-buttons-delete="action.messages.modal.buttons.delete"
        :message-modal-buttons-cancel="action.messages.modal.buttons.cancel"
        :message-modal-buttons-save="action.messages.modal.buttons.save"
      >
      </hcl-favorites>
      <hcl-button
        v-if="action.type !== 'group' && action.type !== 'favorites'"
        :key="action.type + '-' + action.id + '1'"
        class="spacer"
        :type="action.type"
        :variant="action.variant"
        :id="action.id"
        :toolbar-id="toolbarMetaId"
        :title="action.title"
        :disabled="action.disabled"
        :hidden="action.hidden"
        :responsive="action.responsive"
        :href="action.href"
        :label="action.label"
        :icon="action.icon"
        :menu-items="action.menuItems"
        :widget-element="action.widgetElement"
        :widget-attributes="action.widgetAttributes"
        v-on:hcl-button-clicked="onButtonClicked"
        v-on:hcl-list-global-select-all-changed="onListGlobalSelectAllChanged"
        :data="props.data"
      >
      </hcl-button>
    </template>
  </div>
</template>

<script setup lang="ts">
import { inject } from 'vue'

import { useDataStore } from '@/stores/data'

import HclButtonGroup from '@/components/ButtonGroup/ButtonGroup.vue'
import HclButton from '@/components/Button/Button.vue'
import HclFavorites from '@/components/Favorites/Favorites.vue'

import type { DefinitionToolbarContentActions, EventButtonClicked, EventListGlobalSelectAllChanged } from '@/components/types'

interface Props {
  /**
   * Definition as an JS object.
   */
  definition: DefinitionToolbarContentActions
  /**
   * Toolbar Content Actions Data.
   */
  data: any
}

const props = withDefaults(defineProps<Props>(), {
  definition: undefined,
  data: undefined
})

const toolbarMetaId: string = inject('toolbarMetaId', '')

const { link } = useDataStore()

const bucketToolbar = link(toolbarMetaId)

const emit = defineEmits(['hcl-button-clicked', 'hcl-list-global-select-all-changed'])

const onButtonClicked = (event: EventButtonClicked) => {
  emit('hcl-button-clicked', event)
}

const onListGlobalSelectAllChanged = (event: EventListGlobalSelectAllChanged) => {
  emit('hcl-list-global-select-all-changed', event)
}
</script>

<style lang="scss" scoped>
.hcl-toolbar-content-actions {
  display: flex;
  align-items: stretch;
  justify-content: end;

  .toolbar-content-actions-text {
    display: flex;
    align-items: center;
    margin-bottom: 0;

    .content-actions-text-image {
      margin-right: 0.5rem;
    }

    a {
      margin-left: 0.5rem;
    }
  }

  .spacer:not(:last-child) {
    margin-right: 0.5rem;
  }
}
</style>
